// src/frontend/pages/MainPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";
import UvegCertificateNft from "../contractsData/UvegCertificateNft.json";

// --- Contrato con mintImage(address,string) ---
const CONTRACT_ADDRESS = "0x1a94eAB0488CB975326a0A6080E259441aAeafca";

// --- Chainlink MATIC/USD (Polygon Mainnet) ---
const MATIC_USD_FEED = "0xAB594600376Ec9fD91F8e885dADF0CE036862dE0";
const FEED_ABI = [
  "function latestRoundData() view returns (uint80,int256,uint256,uint256,uint80)",
  "function decimals() view returns (uint8)",
];

// --- Bases RAW GitHub ---
const BASES = {
  TERRA: "https://raw.githubusercontent.com/CoachblockchainDapp/TerraImages/main/",
  AQUA:  "https://raw.githubusercontent.com/CoachblockchainDapp/AquaCollection/main/",
  AER:   "https://raw.githubusercontent.com/CoachblockchainDapp/ImagenesEar/main/",
};



// --- Rangos por colección ---
const RANGES = {
  TERRA: { start: 10181, end: 10280 },
  AQUA:  { start: 47,    end: 140   },
  AER:   { start: 1,     end: 97    },
};

// Altura del header fijo y margen superior del contenido
const HEADER_HEIGHT_PX = 92; // ajusta si lo quieres más alto

// === Estilos UI ===
const styles = {
  page: { minHeight: "100vh", background: "#f8fafc" },

  // Header cristal fijo (glassmorphism): transparente, letras negras, alineado
  header: {
    position: "fixed", top: 70, left: 0, right: 0, zIndex: 50,
    background: "rgba(255,255,255,.65)",
    backdropFilter: "saturate(180%) blur(12px)",
    WebkitBackdropFilter: "saturate(180%) blur(12px)",
    color: "#0f172a",
    borderBottom: "1px solid rgba(15,23,42,.08)",
  },
  headerInner: {
    maxWidth: 1200, margin: "0 auto",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 18px",
  },
  brandWrap: { display: "flex", flexDirection: "column" },
  brand: { fontWeight: 800, letterSpacing: .3, margin: 0, fontSize: 18, color: "#0f172a" },
  sub: { marginTop: 2, opacity: .7, fontSize: 12, color: "#0f172a" },

  controls: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },

  btnPrimary: {
    padding: "10px 14px", 
    border: "1px solid rgba(15,23,42,.25)",
    background: "rgba(255,255,255,.6)",
    color: "#0f172a", cursor: "pointer",
  },
  select: {
    padding: "10px 12px", 
    border: "1px solid rgba(15,23,42,.25)",
    background: "rgba(255,255,255,.6)",
    color: "#0f172a", cursor: "pointer",
  },
  headerMetaBar: {
    maxWidth: 1200, margin: "0 auto",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "0 18px 10px", color: "#0f172a", fontSize: 12, opacity: .8,
  },

  // Margen top para no quedar bajo el header fijo
  main: { maxWidth: 1200, margin: `${HEADER_HEIGHT_PX + 16}px auto 40px`, padding: "0 18px" },

  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 24 },
  card: { border: "1px solid #e5e7eb",  overflow: "hidden", background: "#fff", boxShadow: "0 2px 10px rgba(2,8,23,.06)" },
  imgWrap: { padding: 12, background: "#f8fafc" },
  img: { width: "100%", height: "auto", display: "block", maxHeight: 320, objectFit: "cover" },
  body: { padding: 14 },
  title: { margin: 0, fontSize: 15, fontWeight: 800, color: "#0f172a" },
  price: { margin: "8px 0 14px", color: "#475569", fontSize: 14 },
  btnMint: { padding: "10px 14px",  border: "1px solid #0284c7", background: "#06b6d4", color: "#fff", cursor: "pointer" },
  badge: { marginTop: 8, fontSize: 12, color: "#0f172a", background: "#eef2ff", border: "1px solid #c7d2fe", padding: "4px 8px", borderRadius: 8, display: "inline-block" },
  status: { marginTop: 14, color: "#64748b", fontSize: 13 },

  // Modal
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(2,6,23,.7)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  },
  modal: {
    width: "min(560px, 100%)",
    background: "#fff", borderRadius: 14, boxShadow: "0 10px 30px rgba(2,8,23,.3)",
    overflow: "hidden",
  },
  modalHeader: {
    background: "#fee2e2", color: "#7f1d1d", padding: "12px 16px",
    borderBottom: "1px solid #fecaca", fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center",
  },
  modalBody: { padding: 16, whiteSpace: "pre-wrap", color: "#334155", fontSize: 14, lineHeight: 1.4 },
  modalFooter: { padding: 14, display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e5e7eb", background: "#fafafa" },
  btnClose: { padding: "8px 12px", borderRadius: 10, border: "1px solid #e11d48", background: "#ef4444", color: "#fff", cursor: "pointer" },
  btnGhost: { padding: "8px 12px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#fff", color: "#334155", cursor: "pointer" },
};

export default function MainPage() {
  const [account, setAccount] = useState("");
  const [status, setStatus] = useState("Listo");
  const [busy, setBusy] = useState(null);
  const [mintedIdsByCard, setMintedIdsByCard] = useState({});
  const [collection, setCollection] = useState("TODAS");

  const [modal, setModal] = useState(null);
  const closeModal = () => setModal(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Ítems por colección
  const items = useMemo(() => {
    const makeRange = (key) => {
      const base = BASES[key];
      const { start, end } = RANGES[key];
      return Array.from({ length: end - start + 1 }, (_, i) => {
        const id = start + i;
        return { id, url: `${base}${id}.png`, collection: `${key} COLECCIÓN` };
      });
    };

    if (collection === "TODAS") {
      return [
        ...makeRange("TERRA"),
        ...makeRange("AQUA"),
        ...makeRange("AER"),
      ];
    }
    return makeRange(collection);
  }, [collection]);

  const connect = async () => {
    try {
      if (!window.ethereum) throw new Error("MetaMask requerido");
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const addr = await provider.getSigner().getAddress();
      setAccount(addr);
      setStatus("Conectado");
    } catch (e) {
      setStatus("Error: " + (e.message || e));
    }
  };

  // === Oráculo / conversiones ===
  const fmtMatic = (wei) => { try { return Number(ethers.utils.formatEther(wei)).toFixed(6); } catch { return "0"; } };
  const fmtUsd = (num) => { try { return (Math.round(Number(num) * 100) / 100).toFixed(2); } catch { return "0.00"; } };

  const getMaticUsd = async (provider) => {
    const feed = new ethers.Contract(MATIC_USD_FEED, FEED_ABI, provider);
    const [dec, lrd] = await Promise.all([feed.decimals(), feed.latestRoundData()]);
    const price = lrd[1];
    if (!price || price.lte(0)) throw new Error("Precio MATIC/USD inválido");
    return { price, decimals: Number(dec) };
  };

  const quoteWeiFor10USD = async (provider) => {
    const { price, decimals } = await getMaticUsd(provider);
    const usd = ethers.BigNumber.from(10);
    const num = usd.mul(ethers.BigNumber.from(10).pow(decimals)).mul(ethers.BigNumber.from(10).pow(18));
    return num.div(price).mul(101).div(100); // +1% buffer
  };

  const weiToUsd = async (provider, wei) => {
    const { price, decimals } = await getMaticUsd(provider);
    return Number(ethers.utils.formatEther(wei)) * Number(ethers.utils.formatUnits(price, decimals));
  };

  // Token ID desde receipt
  const extractTokenId = (receipt, iface, toAddr) => {
    const ev = (receipt.events || []).find(
      (e) => e.event === "Transfer" && e.args && String(e.args.to).toLowerCase() === String(toAddr).toLowerCase()
    );
    if (ev?.args?.tokenId) return ev.args.tokenId.toString();

    for (const log of receipt.logs || []) {
      try {
        const parsed = iface.parseLog(log);
        if (parsed?.name === "Transfer" && String(parsed.args?.to).toLowerCase() === String(toAddr).toLowerCase()) {
          return parsed.args.tokenId.toString();
        }
        if ((parsed?.name || "").toLowerCase().includes("mint") && parsed.args?.tokenId) {
          return parsed.args.tokenId.toString();
        }
      } catch (_) {}
    }
    return "";
  };

  const handleMint = async (it, idx) => {
    try {
      setBusy(idx);
      setStatus("Preparando firma...");
      if (!window.ethereum) throw new Error("MetaMask requerido");

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const accounts = await provider.listAccounts();
      if (!accounts.length) await provider.send("eth_requestAccounts", []);
      const signer = provider.getSigner();
      const to = await signer.getAddress();

      const valueWei = await quoteWeiFor10USD(provider);

      const ABI = UvegCertificateNft.abi || UvegCertificateNft;
      const nft = new ethers.Contract(CONTRACT_ADDRESS, ABI, signer);
      if (typeof nft.mintImage !== "function")
        throw new Error("El contrato no expone mintImage(address,string)");

      // PRE-CHECK saldo
      setStatus("Revisando saldo…");
      const [balanceWei, gasPrice] = await Promise.all([provider.getBalance(to), provider.getGasPrice()]);
      let gasLimit;
      try {
        gasLimit = await nft.estimateGas.mintImage(to, it.url, { value: valueWei });
      } catch {
        gasLimit = ethers.BigNumber.from(250000);
      }
      const txCostWei = gasLimit.mul(gasPrice);
      const totalWei = valueWei.add(txCostWei);

      if (balanceWei.lt(totalWei)) {
        const needM = fmtMatic(totalWei);
        const haveM = fmtMatic(balanceWei);
        const shortM = fmtMatic(totalWei.sub(balanceWei));
        const [needU, haveU, shortU] = await Promise.all([
          weiToUsd(provider, totalWei),
          weiToUsd(provider, balanceWei),
          weiToUsd(provider, totalWei.sub(balanceWei)),
        ]);
        setStatus("Saldo insuficiente");
        setModal({
          title: "Saldo insuficiente",
          details:
            `Necesitas ~${needM} MATIC (≈ $${fmtUsd(needU)} USD).\n` +
            `Tienes ~${haveM} MATIC (≈ $${fmtUsd(haveU)} USD).\n` +
            `Faltan ~${shortM} MATIC (≈ $${fmtUsd(shortU)} USD).\n\n` +
            `Recarga tu wallet e intenta de nuevo.`,
        });
        return;
      }

      // Enviar tx
      setStatus("Firmando y enviando…");
      const tx = await nft.mintImage(to, it.url, { value: valueWei });
      setStatus("Tx enviada: " + tx.hash);

      const rc = await tx.wait();
      const ok = rc && rc.status === 1;

      const tokenId = extractTokenId(rc, nft.interface, to);
      if (tokenId) setMintedIdsByCard((prev) => ({ ...prev, [idx]: tokenId }));

      setStatus(ok ? `✅ Confirmada${tokenId ? " · Token ID: #"+tokenId : ""}` : "⚠️ Revertida");
    } catch (e) {
      const msg = e?.reason || e?.message || String(e);
      setStatus("Error: " + msg);
      if (msg.toLowerCase().includes("insufficient funds")) {
        setModal({ title: "Saldo insuficiente", details: "No tienes fondos para cubrir el valor y/o el gas. Recarga MATIC e intenta de nuevo." });
      } else if (e?.code === 4001) {
        setModal({ title: "Operación cancelada", details: "Rechazaste la firma en MetaMask." });
      }
    } finally {
      setBusy(null);
    }
  };

  const onImgError = (e) => {
    e.currentTarget.src =
      "data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'>
           <rect width='100%' height='100%' fill='#f1f5f9'/>
           <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
                 font-family='Arial' font-size='16' fill='#64748b'>Vista previa no disponible</text>
         </svg>`
      );
  };

  const rangeText = (() => {
    if (collection === "TODAS") {
      const t = RANGES.TERRA, a = RANGES.AQUA, r = RANGES.AER;
      return `TERRA ${t.start}–${t.end} · AQUA ${a.start}–${a.end} · AER ${r.start}–${r.end}`;
    }
    const { start, end } = RANGES[collection];
    return `${collection} ${start}–${end}`;
  })();

  return (
    <div style={styles.page}>
      {/* HEADER cristal fijo */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.brandWrap}>
            <h1 style={styles.brand}>COLECCIONES NFT</h1>
            
          </div>

          <div style={styles.controls}>
            <select
              aria-label="Seleccionar colección"
              value={collection}
              onChange={(e) => { setCollection(e.target.value); setMintedIdsByCard({}); }}
              style={styles.select}
            >
              <option value="TODAS">TODAS </option>
              <option value="TERRA">TERRA </option>
              <option value="AQUA">AQUA </option>
              <option value="AER">AER </option>
            </select>

            <button onClick={connect} style={styles.btnPrimary}>
              {account ? `Conectado: ${account.slice(0,6)}…${account.slice(-4)}` : "Conectar"}
            </button>
          </div>
        </div>

        
      </header>

      {/* CONTENIDO (con margin-top para despegar del header fijo) */}
      <main style={styles.main}>
        <div style={styles.grid}>
          {items.map((it, ix) => (
            <div key={`${collection}-${it.id}-${ix}`} style={styles.card}>
              <div style={styles.imgWrap}>
                <img
                  src={it.url}
                  alt={`${it.collection} #${it.id}`}
                  style={styles.img}
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  onError={onImgError}
                />
              </div>
              <div style={styles.body}>
                <h4 style={styles.title}>{`${it.collection} · # ${it.id}`}</h4>
                <div style={styles.price}>Precio: 10 USD</div>
                <button
                  style={styles.btnMint}
                  onClick={() => handleMint(it, ix)}
                  disabled={busy === ix}
                  title="Mintear certificado (≈ $10 USD)"
                >
                  {busy === ix ? "Minting..." : "Mint"}
                </button>

                {/* Badge con el token ID generado */}
                {mintedIdsByCard[ix] ? (
                  <div style={styles.badge}>ID: #{mintedIdsByCard[ix]}</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <div style={styles.status}>{status}</div>
      </main>

      {/* MODAL */}
      {modal ? (
        <div
          style={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div id="modal-title">{modal.title}</div>
              <button style={styles.btnGhost} onClick={closeModal} aria-label="Cerrar">✕</button>
            </div>
            <div style={styles.modalBody}>{modal.details}</div>
            <div style={styles.modalFooter}>
              <button style={styles.btnClose} onClick={closeModal}>Cerrar</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
